import { useBreadcrumbs } from '@context/BreadcrumbContext'
import { useSocket } from '@context/SocketContext'
import { useEvents, useTrip, useTripDayInfos } from '@hooks'
import { TripHelper } from '@lib/TripHelper'
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import EmptyState, { EmptyStateButton } from '@components/EmptyState'
import PageLoader from '@components/PageLoader'
import { editTripDestinationsPath } from '@pages/Trips/EditTripDestinationsFlow/paths'
import Board from './Board/Board'
import ImportStatusBanner from './ImportStatusBanner'
import RealtimeStatusBadge from './RealtimeStatusBadge'
import ShareItineraryModal from './ShareItineraryModal/ShareItineraryModal'
import { ItineraryPageContainer, LoadingContainer } from './ItineraryPage.styles'
import { Itinerary } from './types'
import { generateItinerary } from './utils'
import { ErrorNote } from '@components/Notice'

const ItineraryPage: React.FC = () => {
	const { tripId } = useParams<{ tripId: string }>()
	const navigate = useNavigate()
	const { data: trip, isLoading: tripLoading, error: tripError } = useTrip(tripId)
	const { data: eventsData, isLoading: eventsLoading, error: eventsError } = useEvents(tripId)
	const { data: tripDayInfosData } = useTripDayInfos(tripId)
	const [itinerary, setItinerary] = useState<Itinerary | null>(null)
	const [isShareModalOpen, setIsShareModalOpen] = useState(false)
	const { isConnected, joinTrip, leaveTrip } = useSocket()

	const tripStartDate = trip?.startDate ? TripHelper.convertTripDateToInputFormat(trip.startDate) : ''
	const tripEndDate = trip
		? (() => {
				if (!trip.startDate || !trip.dayCount) return ''
				const tripHelper = new TripHelper(trip)
				const endDate = tripHelper.getEndDate()
				return endDate ? endDate.toISOString().split('T')[0] : ''
			})()
		: ''

	// Destinations and dates are edited on their own page (the create flow's
	// Where/When steps); it comes back here when done.
	const openDestinationsEditor = () => {
		if (tripId) navigate(editTripDestinationsPath(tripId))
	}

	// Join trip room when viewing itinerary and connected
	useEffect(() => {
		if (tripId && isConnected) {
			joinTrip(tripId)

			// Leave trip room on unmount
			return () => {
				leaveTrip(tripId)
			}
		}
	}, [tripId, isConnected, joinTrip, leaveTrip])

	useEffect(() => {
		if (trip) {
			setItinerary(generateItinerary(trip, eventsData?.events, tripDayInfosData?.tripDayInfo))
		}
	}, [trip, eventsData, tripDayInfosData])

	// Set breadcrumbs. The live-updates chip is a dev aid; it rides the row's trailing slot.
	useBreadcrumbs(
		trip
			? [
					{ label: trip.name, path: `/trips/${tripId}` },
					{ label: 'Itinerary' },
				]
			: [],
		import.meta.env.DEV ? RealtimeStatusBadge : undefined,
	)

	if (tripLoading || eventsLoading) {
		return <PageLoader label="Loading trip details…" />
	}

	if (tripError || eventsError) {
		return (
			<ErrorNote>Error loading trip: {tripError?.message || eventsError?.message}</ErrorNote>
		)
	}

	// Show trip not found
	if (!trip) {
		return <LoadingContainer>Trip not found</LoadingContainer>
	}

	return (
		<ItineraryPageContainer>
			{tripId && <ImportStatusBanner tripId={tripId} />}

			{!itinerary && trip ? (
				<EmptyState
					art="itinerary"
					title="Set trip dates to begin planning"
					description="You can always change it later."
				>
					<EmptyStateButton onClick={openDestinationsEditor}>Set Trip Dates</EmptyStateButton>
					{tripId && (
						<EmptyStateButton onClick={() => navigate(`/trips/${tripId}/import`)}>
							Import Itinerary
						</EmptyStateButton>
					)}
				</EmptyState>
			) : (
				itinerary && tripId && trip && (
                    <Board
                        itinerary={itinerary}
                        tripId={tripId}
                        tripStartDate={tripStartDate}
                        tripEndDate={tripEndDate}
                        onEditDatesClick={openDestinationsEditor}
                        onShareClick={() => setIsShareModalOpen(true)}
                        onMapClick={() => navigate(`/trips/${tripId}/map`)}
                        onImportClick={() => navigate(`/trips/${tripId}/import`)}
                    />
				)
			)}

		{trip.code && (
			<ShareItineraryModal
				isOpen={isShareModalOpen}
				onClose={() => setIsShareModalOpen(false)}
				tripCode={trip.code}
				isVisibleToPublic={trip.isVisibleToPublic}
			/>
		)}
		</ItineraryPageContainer>
	)
}

export default ItineraryPage
